// This thing lets you add decorators for questions or content that can
// be specific for a curricula, or add no decorators and parsing works
// as expected without changing anything.
// Gives us the ability to have a general parser but also do things to
// all nodes if that's required.
import { wrapP } from './html_utility'
import {
  IClassifyingItem,
  IClassifyingTarget,
  IngestedQuestionData,
  IPlaceholder,
  IQuestionAudioData,
  IQuestionAudioResponse,
  IQuestionClassifying,
  IQuestionDataBase,
  IQuestionDrawingData,
  IQuestionDrawingResponse,
  IQuestionMatching,
  IQuestionMatchingData,
  IQuestionMultiChoiceData,
  IQuestionOrdering,
  IQuestionOrderingData,
  IQuestionPlaceholder,
  IQuestionPlaceholderData,
  IQuestionShortAnswerData,
  IQuestionShortResponse,
  IQuestionShortResponseData,
} from '../components/ingestion/IngestionQuestions'
import {
  AssignmentType,
  GradingType,
  NodeType,
  QuestionType,
} from 'shared/types/ingestion'
import {
  IAssessmentData,
  IDuration,
  INoteData,
  INoteNode,
  IQuestionNode,
  ISimplifiedNode,
} from '../components/ingestion/IngestedTree'
import { nanoid } from 'nanoid'
import { parseHtmlString } from 'shared/utils'
import {
  cleanUpAnswerOptions,
  convertParagraphSpacing,
  createActivity,
  createAssessment,
  createClassificationQuestion,
  createDocumentNode,
  createLessonPlan,
  createMultiAnswerQuestion,
  createMultiPartQuestion,
  createNoteNode,
  createShortAnswer,
  createShortResponse,
  wrapMathIfNeeded,
} from './nodeCreationHelpers'
import {
  contentDecorator,
  genericQuestionType,
  MultiAnswerProps,
  questionDecorator,
} from './types'

export class CustomizableParser {
  questionDecorators: questionDecorator[] = [stripStrongFromAnswers]
  contentDecorators: contentDecorator[] = [convertParagraphSpacing]

  placeholderMarker = '{{response}}'

  addQuestionDecorator(func: questionDecorator) {
    this.questionDecorators.push(func)
  }

  addContentDecorator(...func: contentDecorator[]) {
    this.contentDecorators.push(...func)
  }

  decorateContent(content: string) {
    return this.contentDecorators.reduce((acc, func) => {
      return func(acc)
    }, content)
  }

  decoratedQuestionData<T>(questionData: genericQuestionType<T>) {
    const { description, teacherNote, title, ...decorated } =
      this.questionDecorators.reduce((acc, func) => {
        return func(acc)
      }, questionData)
    return {
      ...decorated,
      title: title && this.decorateContent(title),
      description: this.decorateContent(description),
      teacherNote: teacherNote && this.decorateContent(teacherNote),
    }
  }

  decorateThingWithDescription<T extends { description: string }>(thing: T) {
    thing.description = this.decorateContent(thing.description)
    return thing
  }

  createShortAnswer({
    answer,
    ...data
  }: {
    description: string
    title?: string
    teacherNote?: string
    answer?: string
  }): IngestedQuestionData {
    const questionData: genericQuestionType<IQuestionShortAnswerData> = {
      title: 'Question',
      ...data,
      answer: answer && this.decorateContent(answer),
      studentFacing: true,
      type: QuestionType.ShortAnswer,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return createShortAnswer(decorated)
  }

  createShortResponse(data: {
    description: string
    title?: string
    teacherNote?: string
  }): IQuestionShortResponse {
    const questionData: genericQuestionType<IQuestionShortResponseData> = {
      title: 'Question',
      ...data,
      studentFacing: true,
      type: QuestionType.ShortResponse,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return createShortResponse(decorated)
  }

  createMultiPartQuestion(
    description: string,
    questions: (IQuestionNode | INoteNode)[],
  ) {
    const decorated = this.decorateContent(description)
    return createMultiPartQuestion(decorated, questions)
  }

  createMultiAnswerQuestion(qData: MultiAnswerProps) {
    qData.type = QuestionType.MultipleChoice
    const decorated = this.decoratedQuestionData(qData)
    decorated.answers = decorated.answers.map(
      this.decorateThingWithDescription.bind(this),
    )
    return createMultiAnswerQuestion(decorated)
  }

  createAudioQuestion(data: {
    description: string
    title?: string
  }): IQuestionAudioResponse {
    const questionData: genericQuestionType<IQuestionAudioData> = {
      title: 'Question',
      ...data,
      studentFacing: true,
      type: QuestionType.Audio,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return {
      type: NodeType.Question,
      data: decorated,
    }
  }

  createClassificationQuestion(parts: {
    description: string
    targets: IClassifyingTarget[]
    items: IClassifyingItem[]
    title?: string
    allowAnswerReuse?: boolean
  }): IQuestionClassifying {
    const decorator = this.decorateThingWithDescription.bind(this)
    const question = createClassificationQuestion(parts)
    question.data.targets = question.data.targets.map(decorator)
    question.data.items = question.data.items.map(decorator)
    question.data = this.decoratedQuestionData(question.data)
    return question
  }

  createNoteNode({ value, title, ...rest }: INoteData): INoteNode {
    return createNoteNode({
      value: this.decorateContent(value),
      title: this.decorateContent(title),
      ...rest,
    })
  }

  createDocumentNode<T = ISimplifiedNode>(title: string, nodes: T[]) {
    return createDocumentNode<T>(this.decorateContent(title), nodes)
  }

  createLessonPlan({ title }: { title: string }, nodes: ISimplifiedNode[]) {
    return createLessonPlan({ title: this.decorateContent(title) }, nodes)
  }

  createActivity(
    {
      title,
      ...rest
    }: {
      title: string
      isAssignable?: boolean
      points?: number
      assignment?: AssignmentType
      assessment_grading?: GradingType
      duration?: IDuration
    },
    nodes: ISimplifiedNode[],
  ) {
    return createActivity(
      {
        title: this.decorateContent(title),
        ...rest,
      },
      nodes,
    )
  }

  createAssessment(
    { title, ...rest }: IAssessmentData,
    nodes: ISimplifiedNode[] = [],
  ) {
    return createAssessment(
      {
        title: this.decorateContent(title),
        ...rest,
      },
      nodes,
    )
  }

  createPlaceholder({
    placeholderValue,
    placeholders,
    ...rest
  }: {
    description: string
    placeholderValue: string
    placeholders: IPlaceholder[]
    teacherNote?: string
  }): IQuestionPlaceholder {
    if (!placeholders.length) {
      throw new Error('missing placeholders')
    }
    const questionData: IQuestionPlaceholderData = {
      type: QuestionType.Placeholder,
      placeholder_value: this.decorateContent(placeholderValue),
      placeholders,
      ...rest,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return {
      type: NodeType.Question,
      data: decorated,
    }
  }

  createPlaceholderDND({
    description,
    placeholderValue,
    placeholders,
    allowAnswerReuse,
  }: {
    description: string
    placeholderValue: string
    placeholders: IPlaceholder[]
    allowAnswerReuse?: boolean
  }): IQuestionPlaceholder {
    if (!placeholders.length) {
      throw new Error('missing placeholders')
    }
    const questionData: IQuestionPlaceholderData = {
      description,
      type: QuestionType.PlaceholderDnD,
      placeholder_value: placeholderValue,
      placeholders,
      allowAnswerReuse,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return {
      type: NodeType.Question,
      data: decorated,
    }
  }

  buildPlaceholder({
    title,
    stimulus,
    template,
    answers,
    teacherNote,
  }: {
    title?: string
    stimulus: string
    template: string
    answers: string[]
    teacherNote?: string
  }) {
    answers = cleanUpAnswerOptions(answers)
    const placeholders = answers.map((answer) => {
      return {
        answer: [answer],
        ExternalID: nanoid(10),
      }
    })
    let placeholderValue = template
    placeholders.forEach((placeholder) => {
      placeholderValue = placeholderValue.replace(
        this.placeholderMarker,
        `{{${placeholder.ExternalID}}}`,
      )
    })
    if (placeholderValue.includes(this.placeholderMarker)) {
      throw new Error('Missed a placeholder')
    }
    // Template contains only the blank, return it as short answer
    const isEmptyTemplate =
      !template.replace(this.placeholderMarker, '').trim() &&
      answers.length === 1
    // Placeholder does not support math type answers
    const hasMath = answers.find((a) => a.includes('\\'))
    if ((isEmptyTemplate || hasMath) && answers.length === 1) {
      template = wrapMathIfNeeded(
        template.replace(this.placeholderMarker, '  '),
      )
      return this.createShortAnswer({
        title,
        description: `${wrapP(stimulus)}${wrapP(template)}`,
        teacherNote: answers[0],
        answer: answers[0],
      })
    }
    return this.createPlaceholder({
      description: stimulus,
      placeholders,
      placeholderValue,
      teacherNote,
    })
  }

  buildPlaceholderDND({
    stimulus,
    template,
    answers,
    withDistractors = [...answers],
    allowAnswerReuse,
  }: {
    stimulus: string
    template: string
    answers: string[]
    withDistractors?: string[]
    allowAnswerReuse?: boolean
  }) {
    answers = cleanUpAnswerOptions(answers)
    withDistractors = cleanUpAnswerOptions(withDistractors)
    let placeholderValue = template

    const placeholderHtml = parseHtmlString(template)
    let description = stimulus
    const child = placeholderHtml.body.children.item(0)
    const firstChild = child?.children.item(0)
    if (
      child?.tagName.toLowerCase() !== 'table' &&
      placeholderHtml.body.firstChild?.nodeType === 1 &&
      firstChild &&
      !firstChild.textContent?.includes(this.placeholderMarker)
    ) {
      firstChild.parentElement!.removeChild(firstChild)
      description += firstChild.outerHTML
      placeholderValue = placeholderHtml.body.innerHTML
    }

    const correctAnswers = answers.map((answer) => {
      const placeholder = {
        answer: [answer.replace('&gt;', '>').replace('&lt;', '<')],
        ExternalID: nanoid(10),
      }
      placeholderValue = placeholderValue.replace(
        this.placeholderMarker,
        `{{${placeholder.ExternalID}}}`,
      )
      return placeholder
    })

    const placeholders: IPlaceholder[] = withDistractors.reduce(
      (acc: IPlaceholder[], answer) => {
        if (answers.includes(answer)) {
          return acc
        }
        acc.push({
          answer: [answer],
          ExternalID: nanoid(10),
        })
        return acc
      },
      Object.values(correctAnswers),
    )
    if (placeholderValue.includes(this.placeholderMarker)) {
      throw new Error('Missed a placeholder')
    }
    const questionData = {
      description,
      placeholders,
      placeholderValue,
      allowAnswerReuse,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return this.createPlaceholderDND(decorated)
  }

  createMatching({
    description,
    targets,
    items,
    allowAnswerReuse,
  }: {
    description: string
    targets: IClassifyingTarget[]
    items: IClassifyingItem[]
    allowAnswerReuse?: boolean
  }): IQuestionMatching {
    const questionData: IQuestionMatchingData = {
      type: QuestionType.Matching,
      description,
      allowAnswerReuse,
      targets: targets.map(this.decorateThingWithDescription.bind(this)),
      items: items.map(this.decorateThingWithDescription.bind(this)),
    }
    const decorated = this.decoratedQuestionData(questionData)
    return {
      type: NodeType.Question,
      data: decorated,
    }
  }

  createOrdering({
    description,
    items,
    answer,
  }: {
    description: string
    items: IClassifyingTarget[]
    answer: string[]
  }): IQuestionOrdering {
    const questionData: IQuestionOrderingData = {
      type: QuestionType.Ordering,
      description,
      items,
      answer,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return {
      type: NodeType.Question,
      data: decorated,
    }
  }

  createDrawing(data: {
    description: string
    teacherNote?: string
  }): IQuestionDrawingResponse {
    const questionData: genericQuestionType<IQuestionDrawingData> = {
      title: 'Question',
      ...data,
      studentFacing: true,
      type: QuestionType.Drawing,
    }
    const decorated = this.decoratedQuestionData(questionData)
    return {
      type: NodeType.Question,
      data: decorated,
    }
  }

  createShortAnswerOrResponse(
    data: Partial<IQuestionDataBase> & { description: string },
  ) {
    const description = data.description.toLowerCase()
    const testStrings = ['show your work', 'explain your reasoning']
    if (testStrings.find((test) => description.includes(test))) {
      return this.createShortResponse(data)
    }
    return this.createShortAnswer(data)
  }
}

function stripStrongFromAnswers<T>(questionData: genericQuestionType<T>) {
  if (
    ![QuestionType.MultipleChoice, QuestionType.MultipleSelect].includes(
      questionData.type!,
    )
  )
    return questionData
  const needsStripping = (content: string) => {
    const toTest = content.toLowerCase()
    return toTest.startsWith('<strong') && toTest.split('</strong>').length > 1
  }
  const multiAnswerQuestion =
    questionData as unknown as IQuestionMultiChoiceData
  multiAnswerQuestion.answers.forEach((a) => {
    if (!needsStripping(a.description)) return
    const asDoc = parseHtmlString(a.description)
    const strongElem = asDoc.body.getElementsByTagName('strong').item(0)
    a.description = strongElem!.innerHTML
  })
  return questionData
}
